// components
import { Autocomplete, AutocompleteChangeReason } from '@mui/material';
import TextField from '@mui/material/TextField';

// hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue } from 'usehooks-ts';

// types
import { SearchProps } from './Search.types';

export default function Search<T>(props: SearchProps<T>) {
  const {
    debounceInMS = 500,
    disabled,
    error,
    fullWidth = false,
    helperText,
    label,
    labelProperty,
    loading,
    idProperty,
    minWidth = 220,
    multiple = false,
    noOptionsText,
    onSelected,
    onQueryChange,
    options = [],
    ref,
    showOptionsOnFocus,
    value,
    variant = 'outlined'
  } = props;
  const { t } = useTranslation('common');
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery] = useDebounceValue(query, debounceInMS);

  const handleSelect = (
    option: T | T[] | null | undefined,
    reason: AutocompleteChangeReason
  ) => {
    onSelected?.(option as any, reason);
    setQuery('');
  };

  useEffect(() => {
    if (debouncedQuery.length > 0) {
      onQueryChange?.(debouncedQuery);
    }
  }, [debouncedQuery]);

  return (
    <Autocomplete
      value={value}
      inputValue={query}
      onInputChange={(_, newInput) => {
        setQuery(newInput);
        if (newInput.length === 0) props.onSelected?.(null, 'clear');
      }}
      autoHighlight
      options={
        showOptionsOnFocus || (debouncedQuery && !loading)
          ? options.sort((a, b) => {
              return a[labelProperty] > b[labelProperty] ? 1 : -1;
            })
          : []
      }
      noOptionsText={noOptionsText}
      loadingText={`${t('searching')}...`}
      loading={loading}
      multiple={multiple}
      onChange={(
        _,
        _value: T | T[] | null,
        reason: AutocompleteChangeReason
      ) => {
        const value = multiple ? (_value as T[]) : (_value as T | null);
        handleSelect(value, reason);
      }}
      onBlur={() => setQuery('')}
      ref={ref}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          size="small"
          label={label}
          error={error}
          helperText={helperText}
          fullWidth={fullWidth}
          sx={{
            minWidth,
            width: fullWidth ? '100%' : 220
          }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const query: string = event.target.value;
            setQuery(query);
          }}
        />
      )}
      isOptionEqualToValue={(option: T, v: T) =>
        option[idProperty] === v?.[idProperty]
      }
      disabled={disabled}
      getOptionLabel={(option) => option?.[labelProperty] ?? ''}
    />
  );
}
